import React, { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { FieldProps } from 'formik'
import { Box, Grid, makeStyles } from '@material-ui/core'

import CompactGeofenceRadiusSlider from 'civic-champs-shared/core/CompactGeofenceRadiusSlider'
import ReactGoogleMap from '../../core/location/ReactGoogleMap'
import { DEFAULT_GEOFENCING } from '../../constants/GEO_DATA'
import { MAX_RADIUS_IN_METER, MIN_RADIUS_IN_METER } from '../../helpers/toRoundMetersTo5Feet'
import { StyledInput } from 'civic-champs-shared/core'
import { resolvePlusCodeToCoordinates } from '../../core/location/plus-code-utils'

export { extractPlusCode } from '../../core/location/plus-code-utils'

export interface PlusCodeFieldProps extends FieldProps {
  label?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  hideMap?: boolean
  referenceCoordinates?: { lat: number; lng: number }
}

const canShowMapForValue = (addressValue: PlusCodeFieldProps['field']['value']) => {
  const line1 = addressValue?.line1?.trim()
  if (!line1) {
    return false
  }
  const coords = addressValue?.geofencing?.location?.coordinates
  if (!coords) {
    return false
  }
  const { lat, lng } = coords
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)
}

const useStyles = makeStyles({
  verticalGap: {
    gap: '10px',
  },
  error: {
    color: '#d32f2f',
    fontSize: '12px',
    marginTop: '4px',
  },
})

export const PlusCodeField = (props: PlusCodeFieldProps) => {
  const {
    field: { name, value },
    form,
    label,
    placeholder,
    className,
    disabled,
    hideMap,
    referenceCoordinates,
  } = props

  const formikError = form.touched[name] ? (form.errors[name] as string | undefined) : undefined

  const { geofencing = DEFAULT_GEOFENCING } = value || {}
  const [showMap, setShowMap] = useState(() => canShowMapForValue(value))
  const [error, setError] = useState<string | null>(null)
  const [isResolving, setIsResolving] = useState(false)
  const resolveRequestId = useRef(0)
  const inputValueRef = useRef(value?.line1 ?? '')

  useEffect(() => {
    if (canShowMapForValue(value)) {
      setShowMap(true)
    }
  }, [value?.line1, value?.geofencing?.location?.coordinates?.lat, value?.geofencing?.location?.coordinates?.lng])

  const classes = useStyles()

  const handleChangeRadius = useCallback(
    (newRadius: number) => {
      form.setFieldValue(name, {
        ...value,
        geofencing: {
          ...geofencing,
          radius: newRadius,
        },
      })
    },
    [geofencing, name, value, form],
  )

  const resolveInput = useCallback(
    async (inputValue: string) => {
      const trimmed = inputValue.trim()
      if (!trimmed) {
        setShowMap(false)
        setError(null)
        return
      }

      const requestId = ++resolveRequestId.current
      setIsResolving(true)
      setError(null)

      const result = await resolvePlusCodeToCoordinates({
        input: trimmed,
        referenceCoordinates,
      })

      if (requestId !== resolveRequestId.current) {
        return
      }

      setIsResolving(false)

      if ('error' in result) {
        setShowMap(false)
        setError(result.error)
        return
      }

      form.setFieldValue(name, {
        ...value,
        line1: trimmed,
        ...result.address,
        geofencing: {
          ...geofencing,
          location: {
            coordinates: {
              lat: result.lat,
              lng: result.lng,
            },
            type: 'Point',
          },
        },
      })
      setError(null)
      setShowMap(true)
      void form.validateField(name)
    },
    [form, geofencing, name, referenceCoordinates, value],
  )

  const onChange = (inputValue: string) => {
    inputValueRef.current = inputValue
    setError(null)

    if (!form.touched[name]) {
      form.setFieldTouched(name, true, false)
    }

    form.setFieldValue(name, {
      ...value,
      line1: inputValue,
      city: '',
      state: '',
      zip: '',
      country: '',
      county: '',
    })
  }

  const onInputBlur = () => {
    void resolveInput(inputValueRef.current || value?.line1 || '')
  }

  const onPaste = () => {
    window.setTimeout(() => {
      void resolveInput(inputValueRef.current || value?.line1 || '')
    }, 0)
  }

  return (
    <Grid container direction="column-reverse" className={cn(className, classes.verticalGap)}>
      {showMap && !hideMap && (
        <Grid item sm={6}>
          <Box pr={0}>
            <ReactGoogleMap coordinates={geofencing.location.coordinates} geofenceRadius={geofencing.radius} />
            <CompactGeofenceRadiusSlider
              radius={geofencing.radius}
              min={MIN_RADIUS_IN_METER}
              max={MAX_RADIUS_IN_METER}
              onChange={handleChangeRadius}
            />
          </Box>
        </Grid>
      )}
      <Grid container item sm={12}>
        <Grid item sm={12} xs={12}>
          <StyledInput
            label={label}
            placeholder={placeholder}
            disabled={disabled}
            value={value?.line1 ?? ''}
            onChange={onChange}
            onInputBlur={onInputBlur}
            onPaste={onPaste}
          />
          {isResolving && !error && !formikError && <div className={classes.error}>Resolving Plus Code…</div>}
          {(error || formikError) && <div className={classes.error}>{error || formikError}</div>}
        </Grid>
      </Grid>
    </Grid>
  )
}
